import { For, Match, mergeProps, Switch } from 'solid-js';
import { ColorName, textColorClasses } from '../../style/color-classes';
import { ErrorCatcher } from '../../tools/error-catcher';
import { FontSize, ListProps } from './list.d';
import { Dynamic } from 'solid-js/web';
import clsx from 'clsx';

const defaultProps = {
    fontSize: 'l' as FontSize,
    ordered: false,
};

const fontSizeClasses: Record<FontSize, string> = {
    xs: 'text-paragraphXS',
    s: 'text-paragraphS',
    m: 'text-paragraphM',
    l: 'text-paragraphL',
    xl: 'text-paragraphXL',
    xxl: 'text-paragraphXXL',
};

export const List = (componentProps: ListProps) => {
    const props = mergeProps(defaultProps, componentProps);
    const fontSizeClassName = props.fontSize && fontSizeClasses[props.fontSize];
    const colorClassName = props.color && textColorClasses[props.color as ColorName];

    const listItemClasses = 'pl-2 pb-0 mb-4 [&_a]:underline';
    const tag = props.ordered ? 'ol' : 'ul';

    return (
        <ErrorCatcher componentName="List">
            <Dynamic
                component={tag}
                class={clsx(
                    fontSizeClassName,
                    colorClassName,
                    'max-w-textMaxWidth mb-[2em] mt-4 w-full pl-6',
                    props.ordered ? 'list-decimal' : 'list-disc',
                )}
            >
                <Switch>
                    <Match when={props.items}>
                        <For each={props.items}>
                            {(item: string) => {
                                return <li class={listItemClasses} innerHTML={item} />;
                            }}
                        </For>
                    </Match>
                    <Match when={props.children}>
                        <For each={props.children}>
                            {(child: string) => {
                                return <li class={listItemClasses}>{child}</li>;
                            }}
                        </For>
                    </Match>
                </Switch>
            </Dynamic>
        </ErrorCatcher>
    );
};

List.parseProps = (atts: any) => {
    let parsedListItems: string[] | undefined;
    if (Array.isArray(atts?.content)) {
        parsedListItems = atts.content.map((item: any) => {
            const match = item.innerHTML.match(/<li[^>]*>([\s\S]*)<\/li>/i);
            return match ? match[1].trim() : item.innerHTML.trim();
        });
    } else if (typeof atts?.content === 'string') {
        // Some Gutenberg list block formats store the raw HTML as a single string.
        parsedListItems = Array.from(atts.content.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)).map(
            (m: any) => m[1].trim(),
        );
    }

    return {
        items: parsedListItems,
        fontSize: atts.fontSize || 'l',
        color: atts.color || null,
        ordered: atts.ordered || false,
    };
};
